import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useOrganizationAutoSelect } from './session/useOrganizationAutoSelect.js';
import { useValidOrganizations } from './session/useValidOrganizations.js';
import { useUserSessionPersistence } from './session/useUserSessionPersistence.js';
const SESSION_STORAGE_KEY = 'vectorhub:session:orgId';
const SessionContext = createContext(null);
export function SessionProvider({ children }) {
    const queryClient = useQueryClient();
    const { user, isLoading: isUserLoading } = useCurrentUser();
    const userId = user?.userId ?? null;
    const [currentOrgId, setCurrentOrgIdState] = useState(null);
    // Clear on logout.
    useEffect(() => {
        if (!user)
            setCurrentOrgIdState(null);
    }, [user]);
    // Restore persisted org once per userId.
    useUserSessionPersistence(userId, setCurrentOrgIdState);
    // Available orgs read from Firestore user/{userId}/orgs (same mechanism SC uses — §6.0).
    const { orgIds: availableOrgIds, isLoading: isOrgsLoading } = useValidOrganizations(user);
    const setCurrentOrgId = useCallback((id) => {
        setCurrentOrgIdState(id);
        if (typeof window !== 'undefined') {
            if (id)
                window.localStorage.setItem(SESSION_STORAGE_KEY, id);
            else
                window.localStorage.removeItem(SESSION_STORAGE_KEY);
        }
        void queryClient.invalidateQueries({ queryKey: ['org'] });
    }, [queryClient]);
    const { isAutoSelecting } = useOrganizationAutoSelect(userId, currentOrgId, availableOrgIds, setCurrentOrgId);
    const value = useMemo(() => ({
        currentOrgId,
        setCurrentOrgId,
        availableOrgIds,
        isLoading: isUserLoading || isOrgsLoading || isAutoSelecting,
        isAutoSelecting,
    }), [currentOrgId, setCurrentOrgId, availableOrgIds, isUserLoading, isOrgsLoading, isAutoSelecting]);
    return _jsx(SessionContext.Provider, { value: value, children: children });
}
export function useSessionContext() {
    const ctx = useContext(SessionContext);
    if (!ctx) {
        throw new Error('useSessionManager must be used within a <SessionProvider>.');
    }
    return ctx;
}
//# sourceMappingURL=SessionContext.js.map